import React, { useState } from 'react';
import { Link, useForm, router } from '@inertiajs/react';
import StaffLayout from '@/layouts/StaffLayout';
import ThemeWrapper from '@/components/ThemeWrapper';
import TextInput from '@/components/Forms/TextInput';
import InputError from '@/components/Forms/InputError';
import PrimaryButton from '@/components/Buttons/PrimaryButton';
import SecondaryButton from '@/components/Buttons/SecondaryButton';
import useRoutes from '@/hooks/useRoutes';
import { User } from '@/types';
import { AlertCircle, Eye, EyeOff, Copy, Key } from 'lucide-react';

interface EditProps {
    student: User;
    errors?: Record<string, string>;
    success?: boolean;
    credentials?: {
        email: string;
        password: string;
        url: string;
    };
}

const EditContent: React.FC<EditProps> = ({
    student,
    errors: serverErrors,
    credentials,
}) => {
    const { route } = useRoutes();
    const [showPassword, setShowPassword] = useState(false);
    const [passwordGenerated, setPasswordGenerated] = useState(false);
    const [copied, setCopied] = useState(false);

    const { data, setData, post, processing, errors } = useForm({
        first_name: student.first_name || '',
        last_name: student.last_name || '',
        email: student.email || '',
        password: '',
        new_password: '',
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        post(route('staff-students-edit', { student_id: student.id }).toString());
    };

    const handleCopyCredentials = async () => {
        if (!credentials) return;
        const message = `Te paso tus nuevas credenciales:\nURL: ${credentials.url}\nUsuario: ${credentials.email}\nPassword: ${credentials.password}`;
        try {
            await navigator.clipboard.writeText(message);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            setCopied(false);
        }
    };

    const allErrors = { ...serverErrors, ...errors };

    return (
        <StaffLayout>
            <div className="mx-auto max-w-3xl">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                        Editar Estudiante
                    </h1>
                    <p className="mt-2 text-gray-600 dark:text-neutral-300">
                        Modifica la información del estudiante
                    </p>
                </div>

                <div className="rounded-lg bg-white dark:bg-neutral-800 p-6 shadow">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* First Name */}
                        <TextInput
                            label="Nombre"
                            name="first_name"
                            value={data.first_name}
                            onChange={(value) => setData('first_name', value)}
                            error={allErrors.first_name}
                        />

                        {/* Last Name */}
                        <TextInput
                            label="Apellido"
                            name="last_name"
                            value={data.last_name}
                            onChange={(value) => setData('last_name', value)}
                            error={allErrors.last_name}
                        />

                        {/* Email */}
                        <TextInput
                            label="Email"
                            name="email"
                            type="email"
                            value={data.email}
                            onChange={(value) => setData('email', value)}
                            error={allErrors.email}
                        />

                        {/* Password Generation Button */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-neutral-300 mb-2">
                                Contraseña
                            </label>
                            <button
                                type="button"
                                onClick={() => {
                                    setData('new_password', 'generate');
                                    setPasswordGenerated(true);
                                }}
                                className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-neutral-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-neutral-300 bg-white dark:bg-neutral-800 hover:bg-gray-50 dark:hover:bg-neutral-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                                <Key size={16} className="mr-2" />
                                Generar nueva contraseña
                            </button>
                            {passwordGenerated && (
                                <p className="mt-2 text-sm text-green-600">
                                    ✓ Se generará una nueva contraseña al guardar
                                </p>
                            )}
                        </div>

                        {/* Password */}
                        <div>
                            <label
                                htmlFor="password"
                                className="mb-1 block text-sm font-medium text-gray-500 dark:text-neutral-400"
                            >
                                Nueva Contraseña
                            </label>
                            <div className="relative">
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    id="password"
                                    value={data.password}
                                    onChange={(e) =>
                                        setData('password', e.target.value)
                                    }
                                    className={`block w-full rounded-md border ${
                                        allErrors.password
                                            ? 'border-red-300'
                                            : 'border-gray-300 dark:border-neutral-600'
                                    } px-3 py-2 pr-10 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500`}
                                    placeholder="Dejar en blanco para mantener la actual"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute inset-y-0 right-0 flex items-center pr-3"
                                >
                                    {showPassword ? (
                                        <EyeOff
                                            size={20}
                                            className="text-gray-400 dark:text-neutral-500"
                                        />
                                    ) : (
                                        <Eye
                                            size={20}
                                            className="text-gray-400 dark:text-neutral-500"
                                        />
                                    )}
                                </button>
                            </div>
                            <p className="mt-1 text-xs text-gray-500 dark:text-neutral-400">
                                Dejar en blanco para mantener la contraseña actual.
                                Mínimo 8 caracteres si se proporciona.
                            </p>
                            <InputError error={allErrors.password} />
                        </div>

                        {/* General Error */}
                        {allErrors.general && (
                            <div className="rounded-md bg-red-50 p-4">
                                <div className="flex">
                                    <AlertCircle
                                        size={20}
                                        className="mr-3 text-red-600"
                                    />
                                    <div className="text-sm text-red-700">
                                        {allErrors.general}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Actions */}
                        <div className="flex items-center justify-end space-x-3 border-t pt-4">
                            <Link
                                href={route('staff-students-detail', {
                                    student_id: student.id,
                                }).toString()}
                            >
                                <SecondaryButton type="button">
                                    Cancelar
                                </SecondaryButton>
                            </Link>
                            <PrimaryButton type="submit" disabled={processing}>
                                {processing ? 'Guardando...' : 'Guardar Cambios'}
                            </PrimaryButton>
                        </div>
                    </form>
                </div>
            </div>

            {/* Credentials Modal */}
            {credentials && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="mx-4 w-full max-w-md rounded-lg bg-white dark:bg-neutral-800 p-6">
                        <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                            ✓ Usuario actualizado
                        </h3>
                        <p className="mb-4 text-sm text-gray-600 dark:text-neutral-300">
                            Nueva contraseña generada. Copia el siguiente mensaje para enviarlo al cliente:
                        </p>

                        {/* Copyable Message */}
                        <div className="mb-4 rounded-md border border-gray-200 dark:border-neutral-700 bg-gray-50 dark:bg-neutral-700 p-4">
                            <pre className="whitespace-pre-wrap font-mono text-sm">
{`Te paso tus nuevas credenciales:
URL: ${credentials.url}
Usuario: ${credentials.email}
Password: ${credentials.password}`}
                            </pre>
                        </div>

                        {/* Buttons */}
                        <div className="flex gap-3">
                            <PrimaryButton
                                onClick={handleCopyCredentials}
                                className="flex-1"
                            >
                                <Copy size={16} className="mr-2" />
                                {copied ? 'Copiado!' : 'Copiar mensaje'}
                            </PrimaryButton>
                            <SecondaryButton
                                onClick={() => router.visit(route('staff-students-detail', { student_id: student.id }).toString())}
                            >
                                Cerrar
                            </SecondaryButton>
                        </div>
                    </div>
                </div>
            )}
        </StaffLayout>
    );
};

const Edit: React.FC<EditProps> = (props) => {
    return (
        <ThemeWrapper>
            <EditContent {...props} />
        </ThemeWrapper>
    );
};

export default Edit;
